import {
  CompanyId,
  CustomType,
  DivisionCategoryId,
  GoalWithProgress,
  ProgressType,
  UserCompanyDto,
  UserCustomDailyProgress,
  UserDailyDiaryProgress,
  UserDailyProgress,
} from '../../services/api.types'
import { ActivityView } from '../../views/Activity/store/ActivityReducer'
import { GoalConfig, GoalsConfig } from '../../views/ChallengeAdmin/ChallengeAdmin'
import { c, msgid, ngettext, t } from 'ttag'
import { unitsCountDurationMinuteLong, unitsCountDurationMinuteShort, unitsCountEnergyCalorieLong } from './units'
import { CustomTypeCategory, getCustomUnitText } from '../../services/CustomTypes'
import { getPeriodString } from './date'
import * as React from 'react'
import dayjs from '../../dayjsConfig'
import { yuMuuvPublicCompanyId } from '../../views/Settings/Settings'
import { red } from '../../styles/material-ui-theme-v2'

export const isTotalType = (progressType: ProgressType) => {
  return [ProgressType.ALL, ProgressType.STEPS].includes(progressType)
}

export const isStepsType = (progressType: ProgressType) =>
  [ProgressType.STEPS, ProgressType.COLLECTIVE_STEPS, ProgressType.PERSISTENCE_STEPS].includes(progressType)

/*export const isManualEntryType = (progressType: ProgressType) =>
  [
    ProgressType.CUSTOM,
    ProgressType.COLLECTIVE_CUSTOM,
    ProgressType.PERSISTENCE_CUSTOM,
    ProgressType.PERSISTENCE_CUSTOM_BINARY,
  ].includes(progressType)*/

export const isPersistenceType = (progressType: ProgressType) => {
  return [
    ProgressType.PERSISTENCE_STEPS,
    ProgressType.PERSISTENCE_CALORIES,
    ProgressType.PERSISTENCE_INTENSIVE_MINUTES,
    ProgressType.PERSISTENCE_ALL,
    ProgressType.PERSISTENCE_CUSTOM,
    ProgressType.PERSISTENCE_CUSTOM_BINARY,
  ].includes(progressType)
}

export const isCollectiveType = (progressType: ProgressType) => {
  return [
    ProgressType.COLLECTIVE_STEPS,
    ProgressType.COLLECTIVE_CALORIES,
    ProgressType.COLLECTIVE_INTENSIVE_MINUTES,
    ProgressType.COLLECTIVE_CUSTOM,
  ].includes(progressType)
}

// Goal that should always show its collective total in the front-end, even when the collective
// leaderboard is not visible. Mirrors FORCE_COLLECTIVE_GOAL_ID in the back-end's
// CollectiveGoalProgressUpdater.
export const FORCE_COLLECTIVE_TOTAL_GOAL_ID = 'a23e48ef-f28e-441f-a0dd-2fcfe1f775a2'

export const isForceCollectiveTotal = (goalId: string) => goalId === FORCE_COLLECTIVE_TOTAL_GOAL_ID

// Helper function to get collective goal value, checking goals.collective first
// This supports both old format (collectiveGoal field) and new format (goals.collective)
export const getCollectiveGoalValue = (goal: GoalWithProgress): number => {
  // Try to parse goals config to check for collective goal
  try {
    if (goal.goals) {
      const goalsConfig = JSON.parse(goal.goals) as GoalsConfig
      if (goalsConfig.collective && goalsConfig.collective.enabled) {
        const collectiveConfig = goalsConfig.collective
        // Return the appropriate goal value based on what's set
        if (
          collectiveConfig.totalGoal !== '' &&
          collectiveConfig.totalGoal !== null &&
          collectiveConfig.totalGoal !== undefined
        ) {
          return typeof collectiveConfig.totalGoal === 'number' ? collectiveConfig.totalGoal : 0
        }
        if (
          collectiveConfig.persistenceGoal !== '' &&
          collectiveConfig.persistenceGoal !== null &&
          collectiveConfig.persistenceGoal !== undefined
        ) {
          return typeof collectiveConfig.persistenceGoal === 'number' ? collectiveConfig.persistenceGoal : 0
        }
        if (
          collectiveConfig.indexTarget !== '' &&
          collectiveConfig.indexTarget !== null &&
          collectiveConfig.indexTarget !== undefined
        ) {
          return typeof collectiveConfig.indexTarget === 'number' ? collectiveConfig.indexTarget : 0
        }
      }
    }
  } catch (error) {
    // If parsing fails, fall back to old collectiveGoal field
  }
  // Fall back to old collectiveGoal field for backward compatibility
  return goal.collectiveGoal || 0
}

// Convert a single goal to a multiple goals format
export const convertToMultipleGoalsFormat = (goal: GoalWithProgress): GoalWithProgress => {
  // Only convert if goal.goals is missing (old system)
  // If goals exists, the challenge is already in the new format
  if (goal.goals) {
    return goal
  }

  // TODO: First handle old collective challenges
  // if (isCollectiveType(goal.progressType)) {
  //   return convertCollectiveChallengeToNewFormat(goal)
  // }

  return goal
}

// TODO:
const convertCollectiveChallengeToNewFormat = (goal: GoalWithProgress): GoalWithProgress => {
  // Only convert if collectiveGoal exists
  if (!goal.collectiveGoal) {
    return goal
  }

  // Map collective progress types to their non-collective equivalents
  let newProgressType: ProgressType
  switch (goal.progressType) {
    case ProgressType.COLLECTIVE_STEPS:
      newProgressType = ProgressType.STEPS
      break
    case ProgressType.COLLECTIVE_CALORIES:
      newProgressType = ProgressType.CALORIES
      break
    case ProgressType.COLLECTIVE_INTENSIVE_MINUTES:
      newProgressType = ProgressType.INTENSIVE_MINUTES
      break
    case ProgressType.COLLECTIVE_CUSTOM:
      newProgressType = ProgressType.CUSTOM
      break
    default:
      // Should not happen, but return original if it does
      return goal
  }

  // Create new goals config with collective goal
  // Move collectiveGoal from top-level to goals.collective
  const goalsConfig: GoalsConfig = {
    individual: null,
    collective: {
      enabled: true,
      totalGoal: goal.collectiveGoal,
      //dailyGoal: '',
      persistenceGoal: '',
      indexTarget: '',
    },
    team: null,
    country: null,
    group: {},
  }

  // Return converted goal with new progress type, nullified collectiveGoal, and updated goals config
  return {
    ...goal,
    progressType: newProgressType,
    collectiveGoal: null, // Remove top-level collectiveGoal
    goals: JSON.stringify(goalsConfig), // Set goals config with collective goal
  }
}

export const isSimpleDayType = (activityType: CustomTypeCategory) => {
  return [
    CustomTypeCategory.CAR_FREE_DAYS,
    CustomTypeCategory.DAILY_FLOSSING,
    CustomTypeCategory.ALCOHOL_ABSTINENCE,
    CustomTypeCategory.FINANCE_TRACKING,
    CustomTypeCategory.DAILY_JOURNALING,
    CustomTypeCategory.GRATITUDE_JOURNALING,
    CustomTypeCategory.NATURAL_LIGHT_EXPOSURE,
    CustomTypeCategory.SMOKING_CESSATION,
  ].includes(activityType)
}

export const isActivityIndexType = (progressType: ProgressType) => {
  return [ProgressType.ALL, ProgressType.PERSISTENCE_ALL].includes(progressType)
}

/**
 * Adjust a percentage value for activity index challenges with custom targets.
 * Used when the activity formula target is not the default 100%.
 *
 * @param percentage - The base percentage to adjust
 * @param isTotalActivityIndex - Whether this is an activity index challenge
 * @param activityFormulaTarget - Custom activity formula target (default 100)
 * @returns Adjusted percentage
 */
export const adjustPercentageForActivityIndex = (
  percentage: number,
  isTotalActivityIndex: boolean,
  activityFormulaTarget: number | null | undefined,
): number => {
  if (isTotalActivityIndex && activityFormulaTarget != null && activityFormulaTarget !== 100) {
    return Math.round(percentage * (100 / activityFormulaTarget))
  }
  return percentage
}

/**
 * Calculate the progress percentage for a challenge goal.
 * Handles special case for activity index challenges with custom targets.
 *
 * @param progressValue - Current progress value
 * @param goalValue - Target goal value
 * @param isTotalActivityIndex - Whether this is an activity index challenge
 * @param activityFormulaTarget - Custom activity formula target (default 100)
 * @returns Progress percentage (0-100+)
 */
export const calculateProgressPercentage = (progressValue: number, goalValue: number): number => {
  return goalValue > 0 ? Math.floor((progressValue / goalValue) * 100) : 0
}

export const isPointsType = (progressType: ProgressType) => {
  return [ProgressType.POINTS, ProgressType.PERSISTENCE_POINTS, ProgressType.COLLECTIVE_POINTS].includes(progressType)
}

export const isBonusType = (customTypeCategory: CustomTypeCategory) => {
  return [CustomTypeCategory.BONUS_SIMPLE, CustomTypeCategory.BONUS_WITH_AMOUNT].includes(customTypeCategory)
}

export const isManualEntryOnlyType = (progressType: ProgressType, customType: CustomType | null | undefined) => {
  //return progressType === ProgressType.CUSTOM && !isDistanceType(customType)
  return progressType === ProgressType.CUSTOM && !isAutomatedType(customType)
}

export const isCustomType = (progressType: ProgressType) => {
  return [
    ProgressType.CUSTOM,
    ProgressType.COLLECTIVE_CUSTOM,
    ProgressType.PERSISTENCE_CUSTOM,
    ProgressType.PERSISTENCE_CUSTOM_BINARY,
  ].includes(progressType)
}

export const isDistanceType = (customType: CustomType | null | undefined) => {
  return !!(
    customType &&
    [
      CustomType.DISTANCE_KM,
      CustomType.DISTANCE_MI,
      CustomType.CYCLING_KM,
      CustomType.CYCLING_MI,
      CustomType.RUNNING_KM,
      CustomType.RUNNING_MI,
      CustomType.WALKING_KM,
      CustomType.WALKING_MI,
    ].includes(customType)
  )
}

export const isAutomatedType = (customType: CustomType | null | undefined) => {
  return !!(
    customType &&
    [
      CustomType.STEPS,
      CustomType.CALORIES,
      CustomType.INTENSIVE_MINUTES,
      CustomType.ACTIVITY_INDEX,
      CustomType.CYCLING_KM,
      CustomType.CYCLING_MI,
      CustomType.CYCLING_HR,
      CustomType.CYCLING_MIN,
      CustomType.DISTANCE_KM,
      CustomType.DISTANCE_MI,
      CustomType.EXERCISE_MIN,
      CustomType.EXERCISE_HR,
      // CustomType.MINDFULNESS_MIN,
      // CustomType.MINDFULNESS_HR,
      // CustomType.OUTDOOR_TIME_MIN,
      // CustomType.OUTDOOR_TIME_HR,
      // CustomType.PLANKING_MIN,
      // CustomType.PLANKING_HR,
      CustomType.RUNNING_KM,
      CustomType.RUNNING_MI,
      CustomType.RUNNING_MIN,
      CustomType.RUNNING_HR,
      // CustomType.SLEEP_HR,
      // CustomType.SLEEP_MIN,
      CustomType.SWIMMING_M,
      CustomType.SWIMMING_YD,
      CustomType.SWIMMING_KM,
      CustomType.SWIMMING_MI,
      CustomType.SWIMMING_MIN,
      CustomType.SWIMMING_HR,
      CustomType.WALKING_KM,
      CustomType.WALKING_MI,
      CustomType.WALKING_MIN,
      CustomType.WALKING_HR,
      // CustomType.WATER_DRINKING_GLASS,
      // CustomType.WATER_DRINKING_ML,
      // CustomType.WATER_DRINKING_CUP,
      // CustomType.WATER_DRINKING_OZ,
      CustomType.YOGA_MIN,
      CustomType.YOGA_HR,
      CustomType.STRENGTH_TRAINING_MIN,
      CustomType.STRENGTH_TRAINING_HR,
      CustomType.POINTS,
      CustomType.TOTAL_POINTS,
    ].includes(customType)
  )
}

// Goal type identifier for different goal categories
export type GoalType = 'individual' | 'team' | 'country' | 'group' | 'collective'

/**
 * Returns the AVG suffix text for group-based goals.
 * Individual and collective goals don't show the AVG suffix.
 *
 * @param goalType - The type of goal (individual, team, country, group, collective)
 * @param avgAgg - Whether the goal uses average aggregation
 * @returns The AVG suffix string (e.g. " (AVG)") or empty string
 */
export const getAvgSuffix = (goalType: GoalType, avgAgg: boolean | null | undefined): string => {
  // Individual and collective goals never show AVG suffix
  if (goalType === 'individual' || goalType === 'collective') {
    return ''
  }
  // For team, country, and group goals, show AVG suffix if avgAgg is true
  return avgAgg ? ` (${c('short').t`AVG`})` : ''
}

// Prefix used to identify division category views in activityView strings.
// This is also used in the leaderboard ordering list to store category IDs.
export const DIVISION_CATEGORY_PREFIX = 'DIVISION_CATEGORY_'

/**
 * Builds a leaderboard ID for a division category.
 * This keeps category IDs distinct from standard ActivityView values.
 */
export const buildDivisionCategoryLeaderboardId = (categoryId: string): string => {
  return `${DIVISION_CATEGORY_PREFIX}${categoryId}`
}

/**
 * Checks if a leaderboard ID represents a division category entry.
 * This is safe to call on any string value.
 */
export const isDivisionCategoryLeaderboardId = (leaderboardId?: string | null): boolean => {
  return typeof leaderboardId === 'string' && leaderboardId.startsWith(DIVISION_CATEGORY_PREFIX)
}

/**
 * Extracts the category ID from a division category leaderboard ID.
 * Returns null when the ID is not a category entry.
 */
export const getDivisionCategoryIdFromLeaderboardId = (leaderboardId: string): string | null => {
  if (!isDivisionCategoryLeaderboardId(leaderboardId)) return null
  return leaderboardId.replace(DIVISION_CATEGORY_PREFIX, '')
}

/**
 * Filters a leaderboard list down to division category IDs.
 * This ignores standard leaderboards like INDIVIDUAL or COUNTRY.
 */
export const getDivisionCategoryIdsFromLeaderboards = (leaderboards: string[] | null | undefined): string[] => {
  if (!leaderboards || leaderboards.length === 0) return []
  return leaderboards
    .map((leaderboardId) => getDivisionCategoryIdFromLeaderboardId(leaderboardId))
    .filter((categoryId): categoryId is string => !!categoryId)
}

/**
 * Maps an ActivityView (or string) to the corresponding GoalType and groupCategoryId.
 * Handles both standard ActivityView enum values and custom division category strings.
 *
 * @param activityView - The current activity view (enum value or division category string)
 * @returns Object containing goalType and optional groupCategoryId
 */
export function getGoalTypeFromActivityView(activityView: ActivityView | string | undefined): {
  goalType: GoalType
  groupCategoryId: string | undefined
} {
  // Check if this is a DivisionCategory view (e.g., "DIVISION_CATEGORY_abc123")
  const isDivisionCategoryView = typeof activityView === 'string' && activityView.startsWith(DIVISION_CATEGORY_PREFIX)

  if (isDivisionCategoryView) {
    const categoryId = activityView.replace(DIVISION_CATEGORY_PREFIX, '')
    return { goalType: 'group', groupCategoryId: categoryId }
  }

  // Map standard ActivityView enum values to goal types
  switch (activityView) {
    case ActivityView.INDIVIDUAL:
      return { goalType: 'individual', groupCategoryId: undefined }
    case ActivityView.GROUP:
      return { goalType: 'team', groupCategoryId: undefined }
    case ActivityView.COUNTRY:
      return { goalType: 'country', groupCategoryId: undefined }
    case ActivityView.COLLECTIVE:
      return { goalType: 'collective', groupCategoryId: undefined }
    default:
      // Default to individual if activityView is undefined or unrecognized
      return { goalType: 'individual', groupCategoryId: undefined }
  }
}

/**
 * Checks if the given activityView represents a DivisionCategory view.
 *
 * @param activityView - The activity view to check
 * @returns true if it's a division category view, false otherwise
 */
export function isDivisionCategoryView(activityView: ActivityView | string | undefined): boolean {
  return typeof activityView === 'string' && activityView.startsWith(DIVISION_CATEGORY_PREFIX)
}

/**
 * Extracts the category ID from a DivisionCategory view string.
 *
 * @param activityView - The division category view string
 * @returns The category ID, or null if not a division category view
 */
export function getCategoryIdFromActivityView(activityView: ActivityView | string | undefined): string | null {
  if (!isDivisionCategoryView(activityView)) {
    return null
  }
  return (activityView as string).replace(DIVISION_CATEGORY_PREFIX, '')
}

// export const getChallengeProgressCurrentlyCompact = (goal: GoalWithProgress, goalConfig: GoalConfig | null) => {
//   let total = 0

//   // Custom category groups total can be found in divisionCategoryRanks
//   if (goalConfig?.type === 'group') {
//     if (goalConfig.groupCategoryId) {
//       const parsedDivisionCategoryRanks =
//         typeof goal.divisionCategoryRanks === 'string'
//           ? JSON.parse(goal.divisionCategoryRanks)
//           : goal.divisionCategoryRanks
//       total = parsedDivisionCategoryRanks?.[goalConfig.groupCategoryId]?.total
//     }
//   } else if (goalConfig?.type) {
//     // Other category groups total can be found in goal
//     // if (goalConfig.type === 'individual') {
//     //   total = goal.persistenceTotal
//     // } else if (goalConfig.type === 'team') {
//     //   total = goal.teamTotal
//     // } else if (goalConfig.type === 'country') {
//     //   total = goal.countryTotal
//     // } else if (goalConfig.type === 'collective') {
//     //   total = goal.collectiveTotal
//     // }
//   }

//   return total

//   // const { totalGoal, dailyGoal, persistenceGoal, indexTarget } = goalConfig

//   // if (goalType === 'individual') {
//   //   return isDailyGoalType ? dailyGoal : totalGoal
//   // }

//   // // Use teamTotal if goalType is 'team'
//   // if (goalType === 'team' && teamTotal != null) {
//   //   return teamTotal
//   // }

//   // // Use countryTotal if goalType is 'country'
//   // if (goalType === 'country' && countryTotal != null) {
//   //   return countryTotal
//   // }

//   // // Use countryTotal if goalType is 'country'
//   // if (goalType === 'collective' && collectiveTotal != null) {
//   //   return collectiveTotal
//   // }

//   return 0
// }

// export const getChallengeProgressCurrentlyCompact = (
//   goalConfig: GoalConfig,
//   goalType?: GoalType,
//   isDailyGoalType?: boolean,
// ) => {
//   const { totalGoal, dailyGoal, persistenceGoal, indexTarget } = goalConfig

//   if (goalType === 'individual') {
//     return isDailyGoalType ? dailyGoal : totalGoal
//   }

//   // Use teamTotal if goalType is 'team'
//   if (goalType === 'team' && teamTotal != null) {
//     return teamTotal
//   }

//   // Use countryTotal if goalType is 'country'
//   if (goalType === 'country' && countryTotal != null) {
//     return countryTotal
//   }

//   // Use countryTotal if goalType is 'country'
//   if (goalType === 'collective' && collectiveTotal != null) {
//     return collectiveTotal
//   }

//   return 0
// }

export const getChallengeProgressCurrently = (
  goal: GoalWithProgress,
  goalType?: GoalType,
  groupCategoryId?: DivisionCategoryId,
) => {
  const {
    progressType,
    collectiveTotal,
    totalPercentage,
    persistenceTotal,
    stepsTotal,
    intensiveMinutesTotal,
    teamTotal,
    countryTotal,
  } = goal

  // Custom category groups total can be found in divisionCategoryRanks
  if (goalType === 'group' && groupCategoryId) {
    const parsedDivisionCategoryRanks =
      typeof goal.divisionCategoryRanks === 'string'
        ? JSON.parse(goal.divisionCategoryRanks)
        : goal.divisionCategoryRanks
    return parsedDivisionCategoryRanks?.[groupCategoryId]?.total
  }

  // Use teamTotal if goalType is 'team'
  if (goalType === 'team' && teamTotal != null) {
    return teamTotal
  }

  // Use countryTotal if goalType is 'country'
  if (goalType === 'country' && countryTotal != null) {
    return countryTotal
  }

  // Use countryTotal if goalType is 'country'
  if (goalType === 'collective') {
    return collectiveTotal || 0
  }

  if (isCollectiveType(progressType)) return collectiveTotal
  switch (progressType) {
    case ProgressType.ALL:
      return totalPercentage
    case ProgressType.STEPS:
      return stepsTotal
    case ProgressType.INTENSIVE_MINUTES:
      return intensiveMinutesTotal
    case ProgressType.CUSTOM:
    case ProgressType.PERSISTENCE_STEPS:
    case ProgressType.PERSISTENCE_INTENSIVE_MINUTES:
    case ProgressType.PERSISTENCE_ALL:
    case ProgressType.PERSISTENCE_CUSTOM:
    case ProgressType.PERSISTENCE_CUSTOM_BINARY:
      return persistenceTotal
    default:
      return -1
  }
}

export const getChallengePersonalProgressCurrently = (goal: GoalWithProgress) => {
  const { progressType, totalPercentage, persistenceTotal, caloriesTotal, stepsTotal, intensiveMinutesTotal } = goal
  switch (progressType) {
    case ProgressType.ALL:
      return totalPercentage
    case ProgressType.STEPS:
    case ProgressType.COLLECTIVE_STEPS:
      return stepsTotal
    case ProgressType.COLLECTIVE_CALORIES:
      return caloriesTotal
    case ProgressType.INTENSIVE_MINUTES:
    case ProgressType.COLLECTIVE_INTENSIVE_MINUTES:
      return intensiveMinutesTotal
    case ProgressType.COLLECTIVE_CUSTOM:
    case ProgressType.CUSTOM:
    case ProgressType.PERSISTENCE_STEPS:
    case ProgressType.PERSISTENCE_INTENSIVE_MINUTES:
    case ProgressType.PERSISTENCE_ALL:
    case ProgressType.PERSISTENCE_CUSTOM:
    case ProgressType.PERSISTENCE_CUSTOM_BINARY:
      return persistenceTotal
    default:
      return -1
  }
}

export const getChallengeDailyProgressCurrently = (
  progressType: ProgressType,
  dailyDiary: UserDailyDiaryProgress[],
  progressResults: UserDailyProgress[],
  customProgress: UserCustomDailyProgress[],
  day: string,
  manualEntryEnabled: boolean,
) => {
  // Points
  if (progressType === ProgressType.PERSISTENCE_CUSTOM && customProgress) {
    const d = customProgress?.find((item) => item.date === day)
    return !d ? 0 : d.value
  }
  const isActivityIndex = isActivityIndexType(progressType)
  if (!isActivityIndex) {
    const d = dailyDiary?.find((item) => item.date === day)
    return !d ? 0 : manualEntryEnabled ? d.sum : d.integrationValue
  } else {
    const todayResults = progressResults.find((item) => item.date === day)
    return todayResults ? getPersistenceDailyTotal(todayResults, progressType, manualEntryEnabled) : 0
  }
}

export const getChallengeProgressGoal = (goal: GoalWithProgress, goalType?: GoalType): number => {
  const { progressType, activityFormulaTarget, customGoal, stepsGoal, persistenceGoal } = goal

  // Check if this is a collective goal type and if goals.collective exists
  if (goalType === 'collective' || isCollectiveType(progressType)) {
    // Use helper function to get collective goal value (checks goals.collective first, then collectiveGoal)
    return getCollectiveGoalValue(goal)
  }

  switch (progressType) {
    case ProgressType.ALL:
      return activityFormulaTarget || 100
    case ProgressType.STEPS:
      return stepsGoal
    case ProgressType.COLLECTIVE_CUSTOM:
    case ProgressType.COLLECTIVE_STEPS:
    case ProgressType.COLLECTIVE_CALORIES:
    case ProgressType.COLLECTIVE_INTENSIVE_MINUTES:
      // Use helper function for collective goals (shouldn't reach here due to check above, but kept for safety)
      return getCollectiveGoalValue(goal)
    case ProgressType.CUSTOM:
    case ProgressType.INTENSIVE_MINUTES:
      return customGoal || 0
    case ProgressType.PERSISTENCE_STEPS:
    case ProgressType.PERSISTENCE_INTENSIVE_MINUTES:
    case ProgressType.PERSISTENCE_ALL:
    case ProgressType.PERSISTENCE_CUSTOM:
    case ProgressType.PERSISTENCE_CUSTOM_BINARY:
      return persistenceGoal || 0
    default:
      return -1
  }
}

export const getChallengeProgressPercentage = (goal: GoalWithProgress, goalType?: GoalType): number => {
  const { collectivePercentage, teamTotal, countryTotal, totalPercentage, collectiveTotal } = goal
  const isCollective = isCollectiveType(goal.progressType)

  // For collective goals, use collectivePercentage
  if (isCollective) {
    return collectivePercentage || 0
  }

  // For team goals, calculate percentage from teamTotal
  if (goalType === 'team' && teamTotal != null) {
    const goalValue = getChallengeProgressGoal(goal)
    return goalValue > 0 ? Math.round((teamTotal / goalValue) * 100) : 0
  }

  // For country goals, calculate percentage from countryTotal
  if (goalType === 'country' && countryTotal != null) {
    const goalValue = getChallengeProgressGoal(goal)
    return goalValue > 0 ? Math.round((countryTotal / goalValue) * 100) : 0
  }

  // Use collectiveTotal if goalType is 'collective'
  if (goalType === 'collective' && collectiveTotal != null) {
    const goalValue = getChallengeProgressGoal(goal)
    return goalValue > 0 ? Math.round((collectiveTotal / goalValue) * 100) : 0
  }

  // Default to totalPercentage
  return totalPercentage || 0
}

export const getChallengeProgressUnits = (goal: GoalWithProgress): string => {
  const { progressType, customType, customUnit, persistenceGoal } = goal
  switch (progressType) {
    case ProgressType.ALL:
      return '%'
    case ProgressType.STEPS:
    case ProgressType.COLLECTIVE_STEPS:
      return t`steps`
    case ProgressType.COLLECTIVE_CALORIES:
      return unitsCountEnergyCalorieLong
    case ProgressType.INTENSIVE_MINUTES:
    case ProgressType.COLLECTIVE_INTENSIVE_MINUTES:
      return unitsCountDurationMinuteShort
    //return unitsCountDurationMinuteLong
    case ProgressType.CUSTOM:
    case ProgressType.COLLECTIVE_CUSTOM:
      return getCustomUnitText(customType, customType, customUnit) as string
    case ProgressType.PERSISTENCE_STEPS:
    case ProgressType.PERSISTENCE_INTENSIVE_MINUTES:
    case ProgressType.PERSISTENCE_ALL:
    case ProgressType.PERSISTENCE_CUSTOM:
    case ProgressType.PERSISTENCE_CUSTOM_BINARY:
      return ngettext(msgid`day`, `days`, persistenceGoal || 0)
    default:
      return ''
  }
}

export const getChallengeGoalUnits = (goal: GoalWithProgress): string => {
  const { progressType, customType, customUnit } = goal
  switch (progressType) {
    case ProgressType.ALL:
    case ProgressType.PERSISTENCE_ALL:
      return '%'
    case ProgressType.STEPS:
    case ProgressType.COLLECTIVE_STEPS:
    case ProgressType.PERSISTENCE_STEPS:
      return t`steps`
    case ProgressType.COLLECTIVE_CALORIES:
      return unitsCountEnergyCalorieLong
    case ProgressType.INTENSIVE_MINUTES:
    case ProgressType.COLLECTIVE_INTENSIVE_MINUTES:
    case ProgressType.PERSISTENCE_INTENSIVE_MINUTES:
      return unitsCountDurationMinuteShort
    //return unitsCountDurationMinuteLong
    case ProgressType.CUSTOM:
    case ProgressType.COLLECTIVE_CUSTOM:
    case ProgressType.PERSISTENCE_CUSTOM:
    case ProgressType.PERSISTENCE_CUSTOM_BINARY:
      return getCustomUnitText(customType, customType, customUnit) as string
    default:
      return ''
  }
}

export const getPersistenceDailyTotal = (
  userDailyProgress: UserDailyProgress,
  progressType: ProgressType,
  manualEntryEnabled?: boolean,
) => {
  const { stepsTotal, caloriesTotal, intensiveMinutesTotal, totalPercentage, integrationTotalPercentage } =
    userDailyProgress
  switch (progressType) {
    case ProgressType.STEPS:
    case ProgressType.PERSISTENCE_STEPS:
    case ProgressType.COLLECTIVE_STEPS:
      return stepsTotal
    case ProgressType.CALORIES:
    case ProgressType.PERSISTENCE_CALORIES:
    case ProgressType.COLLECTIVE_CALORIES:
      return caloriesTotal
    case ProgressType.INTENSIVE_MINUTES:
    case ProgressType.PERSISTENCE_INTENSIVE_MINUTES:
    case ProgressType.COLLECTIVE_INTENSIVE_MINUTES:
      return intensiveMinutesTotal
    case ProgressType.ALL:
    case ProgressType.PERSISTENCE_ALL:
      return manualEntryEnabled ? totalPercentage : integrationTotalPercentage
    default:
      return 0
  }
}

export const getDailyValueGoal = (goal: GoalWithProgress, progressType: ProgressType): number => {
  const { dailyPersistenceGoal, dailyStepsGoal, activityFormulaTarget } = goal
  switch (progressType) {
    case ProgressType.ALL:
      return activityFormulaTarget || 100
    case ProgressType.STEPS:
      return dailyStepsGoal || 12000
    case ProgressType.INTENSIVE_MINUTES:
      return 20
    case ProgressType.CUSTOM:
    case ProgressType.PERSISTENCE_ALL:
    case ProgressType.PERSISTENCE_STEPS:
    case ProgressType.PERSISTENCE_CALORIES:
    case ProgressType.PERSISTENCE_INTENSIVE_MINUTES:
    case ProgressType.PERSISTENCE_CUSTOM:
      return dailyPersistenceGoal || 1
    case ProgressType.COLLECTIVE_STEPS:
    case ProgressType.COLLECTIVE_CALORIES:
    case ProgressType.COLLECTIVE_INTENSIVE_MINUTES:
    case ProgressType.COLLECTIVE_CUSTOM:
      // Hard to choose a daily goal for custom, just set it large number.
      return 999999999
    case ProgressType.PERSISTENCE_CUSTOM_BINARY:
    default:
      return 1
  }
}

const getMasterUserCompany = (userCompanies: UserCompanyDto[], iCompanyId: CompanyId) => {
  let companyId = iCompanyId
  if (companyId === 'new') {
    const ownerCompanies = userCompanies
      .filter(
        ({ companyId, ownerCompanyId, userGoalsEnabled }) =>
          companyId != yuMuuvPublicCompanyId && !ownerCompanyId && userGoalsEnabled,
      )
      .sort((a, b) => {
        // Compare the dates to sort in descending order.
        return a.createdAt - b.createdAt
      })
    if (ownerCompanies[0]?.companyId) {
      companyId = ownerCompanies[0].companyId
    }
  }
  let userCompany = userCompanies.find((uc) => uc.companyId === companyId)
  if (!userCompany) return null
  // Allow case if we have manually set period for user created company.
  if (userCompany.startAt || userCompany.paidUntil) return userCompany
  // Otherwise return master company.
  if (userCompany.ownerCompanyId) {
    const ownerCompanyId = userCompany.ownerCompanyId
    userCompany = userCompanies.find((uc) => uc.companyId === ownerCompanyId)
  }
  return userCompany
}

export const getAccountPlan = (userCompanies: UserCompanyDto[], companyId: CompanyId) => {
  if (!userCompanies || !companyId) return null
  const userCompany = getMasterUserCompany(userCompanies, companyId)
  if (!userCompany?.plan) return null
  return userCompany.plan
}

export const getAccountStartDate = (userCompanies: UserCompanyDto[], companyId: CompanyId) => {
  if (!userCompanies || !companyId) return null
  const userCompany = getMasterUserCompany(userCompanies, companyId)
  if (!userCompany) return null
  const dateString = userCompany.plan === 'trial' ? userCompany.trialStartDate : userCompany.startAt
  if (!dateString) return null
  const parsed = dayjs(dateString)
  return parsed.isValid() ? parsed.toDate() : null
}

export const getAccountEndDate = (userCompanies: UserCompanyDto[], companyId: CompanyId) => {
  if (!userCompanies || !companyId) return null
  const userCompany = getMasterUserCompany(userCompanies, companyId)
  if (!userCompany) return null
  const dateString = userCompany.plan === 'trial' ? userCompany.trialEndDate : userCompany.paidUntil
  if (!dateString) return null
  const parsed = dayjs(dateString)
  return parsed.isValid() ? parsed.toDate() : null
}

export const getContractPeriod = (userCompanies: UserCompanyDto[], companyId: CompanyId) => {
  if (!userCompanies || !companyId) return null
  const userCompany = getMasterUserCompany(userCompanies, companyId)
  if (!userCompany) return null
  const startDateString = userCompany.plan === 'trial' ? userCompany.trialStartDate : userCompany.startAt
  const endDateString = userCompany.plan === 'trial' ? userCompany.trialEndDate : userCompany.paidUntil
  const planStartDate = startDateString ? dayjs(startDateString).valueOf() : null
  const planEndDate = endDateString ? dayjs(endDateString).valueOf() : null
  const contractPeriod = planStartDate && planEndDate ? getPeriodString(planStartDate, planEndDate, true) : null
  if (!contractPeriod) return null
  const hasEnded = endDateString && dayjs(endDateString).endOf('day').valueOf() < new Date().getTime()
  return (
    <>
      {userCompany.plan === 'trial' ? t`Trial period` : t`Contract period`}:{' '}
      <strong style={hasEnded ? { color: red, fontWeight: 600 } : { fontWeight: 600 }}>{contractPeriod}</strong>
    </>
  )
}

// Goal type identifier for different goal categories
// Used to specify which goal type to display (individual, team, country, group, collective)
export type ChallengeGoalType = 'individual' | 'team' | 'country' | 'group' | 'collective'

/**
 * Parse goals configuration from a JSON string.
 * Returns null if parsing fails or if the goals string is empty.
 *
 * @param goalsJson - The JSON string containing goal configurations
 * @returns Parsed GoalsConfig object or null
 */
export function parseGoalsConfig(goalsJsonString: string | null | undefined): GoalsConfig | null {
  if (!goalsJsonString) {
    return null
  }
  try {
    return JSON.parse(goalsJsonString) as GoalsConfig
  } catch (error) {
    console.error('Failed to parse goals config:', error)
    return null
  }
}

/**
 * Extract goal-specific configuration based on goal type.
 * Handles individual, team, country, group, and collective goal types.
 *
 * @param goalsConfig - The parsed GoalsConfig object
 * @param goalType - The type of goal to extract (individual, team, country, group, collective)
 * @param groupCategoryId - Required when goalType is 'group' - specifies which division category
 * @returns The specific GoalConfig or null if not found
 */
export function getGoalConfigByType(
  goalsConfig: GoalsConfig | null,
  goalType: ChallengeGoalType | undefined,
  groupCategoryId?: string,
): GoalConfig | null {
  // If no goalType or goalsConfig, return null (backward compatible)
  if (!goalType || !goalsConfig) {
    return null
  }

  // Extract configuration based on goal type
  switch (goalType) {
    case 'group':
      // Group type requires a groupCategoryId to look up the specific group config
      if (!groupCategoryId) {
        return null
      }
      return goalsConfig.group?.[groupCategoryId] || null

    case 'individual':
      return goalsConfig.individual || null

    case 'team':
      return goalsConfig.team || null

    case 'country':
      return goalsConfig.country || null

    case 'collective':
      return goalsConfig.collective || null

    default:
      return null
  }
}

/**
 * Parse goals JSON and extract goal-specific configuration in one step.
 * Combines parseGoalsConfig and getGoalConfigByType for convenience.
 *
 * @param goalsJson - The JSON string containing goal configurations
 * @param goalType - The type of goal to extract (individual, team, country, group, collective)
 * @param groupCategoryId - Required when goalType is 'group' - specifies which division category
 * @returns Object containing both the parsed GoalsConfig and the specific GoalConfig
 */
export function parseAndExtractGoalConfig(
  goalsJsonString: string | null | undefined,
  goalType: ChallengeGoalType | undefined,
  groupCategoryId?: string,
): { goalsConfig: GoalsConfig | null; goalConfig: GoalConfig | null } {
  const goalsConfig = parseGoalsConfig(goalsJsonString)
  const goalConfig = getGoalConfigByType(goalsConfig, goalType, groupCategoryId)
  return { goalsConfig, goalConfig }
}

/**
 * Determines the groupSum value based on activity view and goal configuration.
 * Returns true for "sum" mode, false for "average" mode.
 *
 * - Individual and collective goals always use sum (returns true)
 * - For group-based goals (team, department), checks the goalConfig.avg setting
 * - Returns undefined if no specific value can be determined
 *
 * @param activityView - The current activity view (e.g., INDIVIDUAL, COLLECTIVE, group category)
 * @param goalsJson - The goals JSON string from the goal object
 * @returns true for sum mode, false for average mode, or undefined if not determinable
 */
export function getGroupSumFromActivityView(
  activityView: ActivityView | string | null | undefined,
  goalsJsonString: string | null | undefined,
): boolean | undefined {
  if (!activityView) {
    return undefined
  }

  const { goalType, groupCategoryId } = getGoalTypeFromActivityView(activityView)

  // Individual and collective goals always use sum mode
  if (goalType === 'individual' || goalType === 'collective') {
    return true
  }

  // Extract groupSum from goal config for other goal types (e.g., team, department)
  const { goalConfig } = parseAndExtractGoalConfig(goalsJsonString, goalType, groupCategoryId)

  // Return the inverse of avg (avg=true means groupSum=false)
  if (goalConfig?.avg !== undefined) {
    return !goalConfig.avg
  }

  return undefined
}
